import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import { ListViewFilter, ReturnFields } from '@crm/api'
import { QueryKey } from '@crm/api'
import { client } from '@crm/api/client.gen'
import { InfiniteSelect } from '@crm/components'
import { FieldType, FilterOperatorType, ValueBooleanType } from '@crm/types'
import {
  formatDateToDayjs,
  listTransformKeyToString,
  stringifyUrl
} from '@crm/utils'
import { Options, TDataShape } from '@hey-api/client-axios'
import { useLocales } from '@shared/locales'
import { DATE_TIME_FORMAT, DEFAULT_DATE_FORMAT } from '@shared/utils'
import { InfiniteData, UseInfiniteQueryOptions } from '@tanstack/react-query'
import { Button, Col, DatePicker, Input, Radio, Row, Space } from 'antd'
import { AnyObject } from 'antd/es/_util/type'
import { ButtonProps } from 'antd/lib'
import { AxiosError } from 'axios'
import { useCallback, useEffect, useMemo, useState } from 'react'

type Props = {
  value?: string[]
  filters: (ListViewFilter | null)[]
  viewsFields?: ReturnFields
  numberOfElement: number
  onChange?: (value: string[]) => void
  isDisabled?: boolean
}

const RemoveButton = ({ onClick, disabled }: ButtonProps) => (
  <Button icon={<DeleteOutlined />} onClick={onClick} disabled={disabled} />
)

export const ViewFiltersFieldValue = ({
  viewsFields,
  filters,
  value,
  numberOfElement,
  onChange,
  isDisabled
}: Props) => {
  const { t } = useLocales()

  const [api, setApi] = useState<string>()
  const [valueFieldCount, setValueFieldCount] = useState(1)

  const operator = filters[numberOfElement]?.filter_operator
  const isRange = operator === FilterOperatorType.RANGE

  const isNullOperator = operator === FilterOperatorType.IS_NULL
  const isMultiple = operator === FilterOperatorType.IN

  const findViewFieldType = useMemo(() => {
    if (!filters?.[numberOfElement]?.field_name) return undefined

    return viewsFields?.fields.find(
      (field) =>
        filters[numberOfElement]?.field_name === field.name ||
        filters[numberOfElement]?.field_name === field.filter_name
    )
  }, [filters, viewsFields, numberOfElement])

  const getQueryFunction = useCallback(
    (
      options: Options<TDataShape>
    ): UseInfiniteQueryOptions<
      AnyObject,
      AxiosError<Error, unknown>,
      InfiniteData<AnyObject>,
      AnyObject,
      QueryKey<Options<TDataShape>>,
      | number
      | Pick<
          QueryKey<Options<TDataShape>>[0],
          'body' | 'headers' | 'path' | 'query'
        >
    > => {
      const queryKey: QueryKey<Options<TDataShape>> = [
        {
          query: options.query ?? {},
          path: options.path ?? {},
          body: options.body,
          headers: options.headers
        }
      ] as QueryKey<Options<TDataShape>>

      return {
        queryKey,
        queryFn: async (context) => {
          if (!api) {
            throw new Error('API endpoint is not defined')
          }

          const pageParam =
            typeof context.pageParam === 'number' ? context.pageParam : 1

          const queryParams = {
            ...(options.query as Record<string, unknown>),
            page: pageParam,
            per_page: 20
          }

          const response = await client.request({
            method: 'GET',
            url: stringifyUrl(`${api}`, queryParams ?? {})
          })

          const data = response.data as {
            results?: unknown[]
            next?: string | number | null
            count?: number
          }

          return {
            results: data?.results || (Array.isArray(data) ? data : []),
            next: data?.next
          }
        },
        getNextPageParam: (lastPage) => {
          const next = lastPage?.next

          return typeof next === 'number' ? next : undefined
        },
        initialPageParam: 1,
        enabled: !!api
      }
    },
    [api]
  )

  const updateValueAtIndex = useCallback(
    (index: number, newValue: string) => {
      onChange?.(
        Array.from({ length: valueFieldCount }, (_, i) =>
          i === index ? newValue : value?.[i] ?? ''
        )
      )
    },
    [onChange, value, valueFieldCount]
  )

  const renderMultiple = useCallback(
    (renderInput: (index: number) => React.ReactNode) =>
      Array.from({ length: valueFieldCount }, (_, i) => (
        <Col key={i} span={24}>
          <Space.Compact block>
            {valueFieldCount > 1 && isMultiple && (
              <RemoveButton
                onClick={() => setValueFieldCount((c) => c - 1)}
                disabled={isDisabled}
              />
            )}
            {renderInput(i)}
          </Space.Compact>
        </Col>
      )),
    [valueFieldCount, isMultiple, isDisabled]
  )

  const renderDateInputs = useCallback(
    (format: string, showTime = false) =>
      renderMultiple((i) => (
        <DatePicker
          value={value?.[i] ? formatDateToDayjs(value[i]) : null}
          onChange={(v) => updateValueAtIndex(i, v ? v.toISOString() : '')}
          format={format}
          showTime={showTime}
          disabled={isDisabled}
        />
      )),
    [renderMultiple, updateValueAtIndex, value, isDisabled]
  )

  useEffect(() => {
    if (filters) {
      setValueFieldCount(filters[numberOfElement]?.value?.length || 1)
    }
  }, [filters, numberOfElement])

  useEffect(() => {
    setApi(findViewFieldType?.props?.api)
  }, [findViewFieldType])

  const renderFiltersValueFields = useMemo(() => {
    if (isNullOperator) {
      return (
        <Col>
          <Radio.Group
            value={value?.[0]}
            onChange={({ target }) => {
              if (value) {
                onChange?.([target.value as string])
              }
            }}
            options={[
              {
                label: t('buttons.yes'),
                value: ValueBooleanType.TRUE
              },
              {
                label: t('buttons.no'),
                value: ValueBooleanType.FALSE
              }
            ]}
            disabled={isDisabled}
          />
        </Col>
      )
    } else {
      switch (findViewFieldType?.type) {
        case FieldType.SELECT:
          return (
            <Col>
              <InfiniteSelect<TDataShape, AnyObject, Record<string, unknown>>
                mode={isMultiple ? 'multiple' : undefined}
                getQueryFunction={getQueryFunction}
                value={value}
                onChange={onChange}
                getOptions={({ options }) =>
                  listTransformKeyToString(options || [])
                }
                disabled={isDisabled}
                className="w-full select-min-width"
              />
            </Col>
          )

        case FieldType.DATE_TIME:
          return isRange ? (
            <DatePicker.RangePicker
              placement="bottomRight"
              value={
                value && [
                  value[0] ? formatDateToDayjs(value[0]) : null,
                  value[1] ? formatDateToDayjs(value[1]) : null
                ]
              }
              onChange={(v) =>
                onChange?.(
                  (v || []).map((date) => (date ? date.toISOString() : ''))
                )
              }
              showTime
              format={DATE_TIME_FORMAT}
              disabled={isDisabled}
            />
          ) : (
            renderDateInputs(DATE_TIME_FORMAT, true)
          )

        case FieldType.DATE:
          return isRange ? (
            <Col>
              <DatePicker.RangePicker
                placement="bottomRight"
                format={DEFAULT_DATE_FORMAT}
                value={
                  value && [
                    value[0] ? formatDateToDayjs(value[0]) : null,
                    value[1] ? formatDateToDayjs(value[1]) : null
                  ]
                }
                onChange={(v) =>
                  onChange?.(v ? v.map((date) => date?.toString() || '') : [])
                }
                disabled={isDisabled}
              />
            </Col>
          ) : (
            renderDateInputs(DEFAULT_DATE_FORMAT)
          )

        case FieldType.NUMBER:
          if (isRange) {
            return (
              <>
                {[0, 1].map((i) => (
                  <Col key={i} span={12}>
                    <Input
                      type="number"
                      placeholder={i === 0 ? t('main.from') : t('main.to')}
                      value={value?.[i] ?? ''}
                      onChange={(e) => updateValueAtIndex(i, e.target.value)}
                      disabled={isDisabled}
                    />
                  </Col>
                ))}
              </>
            )
          }

          return renderMultiple((i) => (
            <Input
              type="number"
              value={value?.[i] ?? ''}
              onChange={(e) => updateValueAtIndex(i, e.target.value)}
              disabled={isDisabled}
            />
          ))

        default:
          return renderMultiple((i) => (
            <Input
              value={value?.[i] ?? ''}
              onChange={(e) => updateValueAtIndex(i, e.target.value)}
              disabled={isDisabled}
            />
          ))
      }
    }
  }, [
    t,
    isDisabled,
    isNullOperator,
    isMultiple,
    isRange,
    findViewFieldType,
    value,
    onChange,
    getQueryFunction,
    updateValueAtIndex,
    renderMultiple,
    renderDateInputs
  ])

  return (
    <Row gutter={[4, 4]}>
      {renderFiltersValueFields}

      {findViewFieldType?.type !== FieldType.SELECT &&
        filters[numberOfElement]?.filter_operator === FilterOperatorType.IN && (
          <Col span={24}>
            <Button
              type="primary"
              size="small"
              icon={<PlusOutlined />}
              onClick={() => setValueFieldCount((prevState) => prevState + 1)}
              disabled={isDisabled}
            >
              {t('generic.add_value')}
            </Button>
          </Col>
        )}
    </Row>
  )
}
